"use client";

import { useCallback, useRef } from "react";
import Link from "next/link";
import { Coins } from "lucide-react";
import { useAppSelector } from "@/store/store";
import { useGetCreditBalanceQuery } from "@/store/api/creditApi";
import { formatCredits } from "@/lib/utils/credit.utils";
import { cn } from "@/lib/utils/utils";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

/**
 * Available-credit indicator for the top bar.
 *
 * Shares the RTK Query cache with the usage screens, so having it mounted on
 * every page costs one request rather than one per screen. It renders nothing
 * until the session is known, since the balance endpoint needs the cookie.
 *
 * ## Why it re-reads on hover
 *
 * Everything that spends credit invalidates `CreditBalance` when it lands, but
 * that only covers movements this browser tab made. A movement made anywhere
 * else — the super-admin console in another tab, a teammate drawing on a shared
 * pool, a queued debit draining a moment after the request that filed it
 * resolved — leaves this badge sitting on a figure it believes is fresh. It is
 * mounted app-wide, so `keepUnusedDataFor` never expires it, and creditApi
 * deliberately does not refetch on focus.
 *
 * So the moment somebody looks at the number is treated as a request for the
 * real one. Throttled, because a pointer crossing the top bar should not be a
 * request each time.
 */
const HOVER_REFETCH_THROTTLE_MS = 5_000;

export function CreditBalanceBadge({ className }: { className?: string }) {
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);

  const { data, isLoading, isError, refetch } = useGetCreditBalanceQuery(undefined, {
    skip: !isAuthenticated,
  });

  const lastHoverFetchRef = useRef(0);
  const refreshOnHover = useCallback(() => {
    const now = Date.now();
    if (now - lastHoverFetchRef.current < HOVER_REFETCH_THROTTLE_MS) return;
    lastHoverFetchRef.current = now;
    refetch();
  }, [refetch]);

  if (!isAuthenticated) return null;

  if (isLoading) {
    return <Skeleton className={cn("h-7 w-28", className)} />;
  }

  const balance = data?.runningBalance ?? 0;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Link
          href="/settings/usage"
          aria-label="Available credits"
          onMouseEnter={refreshOnHover}
          onFocus={refreshOnHover}
          className={cn(
            "flex items-center gap-1.5 rounded-md border border-border bg-muted/40 px-2.5 py-1 text-sm transition-colors hover:bg-muted",
            className
          )}
        >
          <Coins className="h-4 w-4 text-amber-500" />
          <span className="font-semibold text-foreground">
            {isError ? "—" : formatCredits(balance)}
          </span>
          <span className="text-xs text-muted-foreground">credits</span>
        </Link>
      </TooltipTrigger>

      <TooltipContent side="bottom">
        {isError ? (
          "Credit balance is unavailable right now"
        ) : (
          <span className="text-xs">
            {formatCredits(data?.consumedAmount)} used of{" "}
            {formatCredits(data?.totalCredit)} — view usage
          </span>
        )}
      </TooltipContent>
    </Tooltip>
  );
}

export default CreditBalanceBadge;
